/**
 * LGPD Consent Banner
 * Shows once per browser session (remembered via localStorage).
 * Registra o consentimento via POST /v1/protecao-de-dados/consentimento.
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, X, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/axios";

const STORAGE_KEY = "lgpd_consent_v1";

interface ConsentState {
  analytics: boolean;
  marketing: boolean;
}

export function LgpdBanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [consents, setConsents] = useState<ConsentState>({
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      // Delay slightly so it doesn't flash on page load
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const record = async (granted: ConsentState) => {
    try {
      await api.post("/v1/protecao-de-dados/consentimento", {
        version: "1.0",
        consents: [
          { type: "essential", granted: true },
          { type: "analytics", granted: granted.analytics },
          { type: "marketing", granted: granted.marketing },
        ],
      });
    } catch {
      // Fail silently — consent will be re-asked on next session
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...granted, essential: true, ts: Date.now() }));
    setVisible(false);
  };

  const acceptAll = () => record({ analytics: true, marketing: true });
  const acceptSelected = () => record(consents);
  const rejectAll = () => record({ analytics: false, marketing: false });

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ type: "spring", stiffness: 260, damping: 28 }}
          className="relative z-20 mx-4 mt-4 md:mx-7"
        >
          <div className="glass rounded-2xl border border-border/60 p-4 shadow-lg shadow-black/20">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-xl bg-primary/10">
                  <Shield className="w-4 h-4 text-primary" />
                </div>
                <p className="font-semibold text-sm">Privacidade e cookies (LGPD)</p>
              </div>
              <button
                onClick={rejectAll}
                aria-label="Fechar aviso e usar somente cookies essenciais"
                className="p-1 rounded-lg hover:bg-muted/50 transition-colors shrink-0"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            <p className="max-w-3xl text-xs text-muted-foreground leading-relaxed mb-3">
              Utilizamos cookies essenciais para o funcionamento do sistema. Com sua permissão,
              também coletamos dados de uso para melhorar a experiência.
            </p>

            {/* Expandable settings */}
            <button
              onClick={() => setExpanded((e) => !e)}
              className="flex items-center gap-1 text-xs text-primary hover:underline mb-3"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              Personalizar preferências
            </button>

            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden mb-3 space-y-2"
                >
                  {/* Essential — always on */}
                  <div className="flex items-center justify-between p-2.5 rounded-xl bg-muted/20">
                    <div>
                      <p className="text-xs font-medium">Essenciais</p>
                      <p className="text-xs text-muted-foreground">Login, segurança, sessão</p>
                    </div>
                    <span className="text-xs text-emerald-400 font-medium">Sempre ativo</span>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-center justify-between p-2.5 rounded-xl bg-muted/20">
                    <div>
                      <p className="text-xs font-medium">Analytics</p>
                      <p className="text-xs text-muted-foreground">Uso de páginas e recursos</p>
                    </div>
                    <button
                      onClick={() => setConsents((c) => ({ ...c, analytics: !c.analytics }))}
                      role="switch"
                      aria-checked={consents.analytics}
                      aria-label="Permitir cookies de análise"
                      className={cn(
                        "relative w-9 h-5 rounded-full transition-colors",
                        consents.analytics ? "bg-primary" : "bg-muted/50 border border-border/50",
                      )}
                    >
                      <span className={cn(
                        "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform",
                        consents.analytics ? "translate-x-4" : "translate-x-0.5",
                      )} />
                    </button>
                  </div>

                  {/* Marketing */}
                  <div className="flex items-center justify-between p-2.5 rounded-xl bg-muted/20">
                    <div>
                      <p className="text-xs font-medium">Marketing</p>
                      <p className="text-xs text-muted-foreground">Comunicações e novidades</p>
                    </div>
                    <button
                      onClick={() => setConsents((c) => ({ ...c, marketing: !c.marketing }))}
                      role="switch"
                      aria-checked={consents.marketing}
                      aria-label="Permitir cookies de marketing"
                      className={cn(
                        "relative w-9 h-5 rounded-full transition-colors",
                        consents.marketing ? "bg-primary" : "bg-muted/50 border border-border/50",
                      )}
                    >
                      <span className={cn(
                        "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform",
                        consents.marketing ? "translate-x-4" : "translate-x-0.5",
                      )} />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions */}
            <div className="flex max-w-md gap-2">
              <button
                onClick={rejectAll}
                className="flex-1 py-2 rounded-xl border border-border/50 text-xs font-medium hover:bg-muted/30 transition-colors"
              >
                Só essenciais
              </button>
              {expanded ? (
                <button
                  onClick={acceptSelected}
                  className="flex-1 py-2 rounded-xl bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
                >
                  Salvar seleção
                </button>
              ) : (
                <button
                  onClick={acceptAll}
                  className="flex-1 rounded-xl bg-primary py-2 text-xs font-medium text-white transition-opacity hover:opacity-90 dark:bg-linear-to-r dark:from-primary dark:to-secondary"
                >
                  Aceitar todos
                </button>
              )}
            </div>

            <p className="text-xs text-muted-foreground/50 text-center mt-2">
              Conforme LGPD (Lei 13.709/2018) · <a href="#" className="underline">Política de Privacidade</a>
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
